import { EventWithDetail, QueryEventsRequest, TicketTierType } from "@garther/interfaces";
import { Spinner } from "@garther/ui/components";
import { cn } from "@garther/ui/libs";
import { useNavigate } from "@tanstack/react-router";

import { useEventsPagination } from "@/hooks/queries/use-events-query";
import { trackBookingStart, trackEventView } from "@/libs/analytics";

import { EventCard } from "./event-card";
import { Pagination } from "./pagination";

type EventsProps = Partial<Omit<QueryEventsRequest, "page">> & {
  classname?: string;
  enablePagination?: boolean;
};

export function Events({ classname, enablePagination = true, ...filters }: EventsProps) {
  const navigate = useNavigate();

  const { events, isLoading, error, pagination, setPage, currentPage } = useEventsPagination({
    enabled: true,
    ...filters,
    limit: filters.limit ?? 6
  });

  const handleEventSelect = (eventId: string) => {
    trackEventView(eventId);
    navigate({ to: "/event/$id", params: { id: eventId } });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleEventBook = (eventId: string) => {
    trackBookingStart(eventId, TicketTierType.GROUP);
    navigate({ to: "/booking/$eventId", params: { eventId } });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (isLoading) {
    return (
      <div className="w-full min-h-[50vh] flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full min-h-[50vh] flex items-center justify-center"> Error: {error.message}</div>
    );
  }

  return (
    <div className="w-full">
      <div
        className={cn("grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 md:gap-2 lg:gap-6", classname)}
      >
        {events.map((event: EventWithDetail) => (
          <EventCard key={event.id} event={event} onSelect={handleEventSelect} onBook={handleEventBook} />
        ))}
      </div>

      {/* Pagination */}
      {enablePagination && pagination && (
        <Pagination currentPage={currentPage} totalPages={pagination.totalPages} onPageChange={setPage} />
      )}
    </div>
  );
}
